import { useEffect, useRef } from "react";

export const useSetupVideoAudioContext = (
  video: HTMLVideoElement | null | undefined,
  playChannel: number,
  src: string | null | undefined,
  handleAudio: boolean
) => {
  const audioContext = useRef<AudioContext | null>(null);
  const splitterNode = useRef<ChannelSplitterNode | null>(null);
  const mergerNode = useRef<ChannelMergerNode | null>(null);
  const pannerNodeLeft = useRef<StereoPannerNode | null>(null);
  const pannerNodeRight = useRef<StereoPannerNode | null>(null);
  const pannerNodeCenter = useRef<StereoPannerNode | null>(null);
  const channelCount = useRef<number>(2);
  const audioSrc = useRef<MediaElementAudioSourceNode | null>(null);

  useEffect(() => {
    splitterNode.current?.disconnect();
    mergerNode.current?.disconnect();
    pannerNodeLeft.current?.disconnect();
    pannerNodeRight.current?.disconnect();
    pannerNodeCenter.current?.disconnect();
    audioSrc.current?.disconnect();

    if (!handleAudio) {
      return;
    }

    if (video && playChannel >= 0) {
      if (!audioContext.current) {
        audioContext.current = new AudioContext();
      }
      if (!audioSrc.current) {
        audioSrc.current = audioContext.current.createMediaElementSource(video);
      }
      channelCount.current = audioContext.current.destination.maxChannelCount;

      const channelCountForSplitter = channelCount.current;

      if (!splitterNode.current)
        splitterNode.current = audioContext.current.createChannelSplitter(channelCountForSplitter);

      if (!mergerNode.current) {
        mergerNode.current = audioContext.current.createChannelMerger(channelCountForSplitter);
      }

      if (!pannerNodeLeft.current) {
        pannerNodeLeft.current = audioContext.current.createStereoPanner();
        pannerNodeLeft.current.pan.setValueAtTime(-1, audioContext.current.currentTime);
      }

      if (!pannerNodeRight.current) {
        pannerNodeRight.current = audioContext.current.createStereoPanner();
        pannerNodeRight.current.pan.setValueAtTime(1, audioContext.current.currentTime);
      }

      if (!pannerNodeCenter.current) {
        pannerNodeCenter.current = audioContext.current.createStereoPanner();
        pannerNodeCenter.current.pan.setValueAtTime(0, audioContext.current.currentTime);
      }
      audioSrc.current.connect(splitterNode.current!);

      if (playChannel === 0) {
        splitterNode.current!.connect(pannerNodeCenter.current, 0);
        pannerNodeCenter.current!.connect(audioContext.current!.destination);
      }
      if (playChannel === 1) {
        splitterNode.current!.connect(pannerNodeCenter.current, 1);
        pannerNodeCenter.current!.connect(audioContext.current!.destination);
      }

      if (playChannel === 2) {
        splitterNode.current!.connect(pannerNodeLeft.current, 0);
        pannerNodeLeft.current!.connect(mergerNode.current!, 0, 0);
        splitterNode.current!.connect(pannerNodeRight.current, 0);
        pannerNodeRight.current!.connect(mergerNode.current!, 0, 1);

        mergerNode.current!.connect(audioContext.current!.destination);
      }
    }
  }, [playChannel, src, handleAudio]);
};
